import React from "react";
import {
  MDBRow,
  MDBCol,
  MDBCard,
  MDBCardBody,
  MDBCardHeader,
  MDBBtn,
  MDBTable,
  MDBTableHead,
  MDBTableBody,
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBSwitch,
  MDBInput,
  MDBIcon,
} from "mdb-react-ui-kit";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import { MfmImageUploader } from "Projects/CC_cubi_clan_project/GlobalComponents/MfmImageUploader/MfmImageUploader";
import { getFileVariants, mfmFramedUrl, mfmVariantUrl } from "Projects/CC_cubi_clan_project/Utils/MfmService";
import { PopConfirm } from "Projects/CC_cubi_clan_project/GlobalComponents/PopConfirm/PopConfirm";
import { CustomCarousel } from "../../Components/CustomCarousel/CustomCarousel";

import { doFetch } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";

import "./CarouselManager.css";

const CAROUSEL_FORMAT_KEY = "ratio:1246:400";
const CAROUSEL_WIDTH = 1246;
const CAROUSEL_HEIGHT = 400;

// Helpers to convert between the frontend representation of carousel items
// and the backend representation expected by the API. The frontend uses
// `id`, `image` (base64 data URI), `title`, `description`, `link` and
// boolean `active`. The backend expects fields like `IDCarousel`,
// `base64img`, and a short `active` value. These functions perform
// the necessary mapping in both directions.

const toBackendItem = (item) => ({
  IDCarousel: item?.id ?? null,
  base64img: null,
  imageUuid: item?.imageUuid ?? null,
  title: item?.title ?? null,
  description: item?.description ?? null,
  link: item?.link ?? null,
  active: item?.active ? 1 : 0,
});

const fromBackendItem = (row) => {
  const isActive =
    row?.active === 1 ||
    row?.active === "1" ||
    row?.active === true ||
    row?.active === "true";
  const imageUrl = row?.imageUuid
    ? mfmFramedUrl(row.imageUuid, 1246, 400, CAROUSEL_FORMAT_KEY)
    : (row?.base64img ? `data:image/jpeg;base64,${row.base64img}` : "");
  return {
    id: row?.IDCarousel ?? null,
    image: imageUrl,
    imageUuid: row?.imageUuid ?? null,
    title: row?.title ?? "",
    description: row?.description ?? "",
    link: row?.link ?? "",
    active: isActive,
  };
};

/**
 * Componente de clase encargado de gestionar el carrusel principal.
 *
 * Muestra el carrusel actual en una tarjeta y ofrece una botonera para
 * abrir un modal de administración. Dentro del modal se lista cada
 * elemento del carrusel en una tabla con acciones para editar,
 * activar/desactivar o eliminar el registro. También se puede crear
 * nuevos elementos.
 *
 * Todo el código de lógica está encapsulado en este componente para
 * simplificar la vista que lo utiliza. Los métodos de servicio que
 * recuperan o persisten datos son ficticios y se deberán adaptar a
 * vuestra API cuando esté disponible.
 */
class CarouselManager extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      items: [],
      loading: false,
      showModal: false,
      modalContent: "table", // 'table' o 'form'
      editingItem: null, // null indica nuevo
      showDeleteConfirm: false,
      itemToDelete: null,
    };
    this._variantRequestId = 0;
  }

  componentDidMount() {
    this.loadItems();
  }

  /**
   * Recupera los elementos del carrusel desde el servicio. Por ahora
   * devuelve datos simulados. Deberás sustituir la lógica por
   * peticiones reales cuando haya una API disponible.
   */
  async loadItems() {
    this.setState({ loading: true });
    if (this.props.onLoading) this.props.onLoading(true);
    try {
      // Llamada real al servicio para obtener los elementos del carrusel.
      const response = await doFetch("TEAN", "getCarousel", { method: "GET" });
      const data = await response.json();
      const rawItems = data && data.DATA ? data.DATA : [];
      // Convertir del formato backend al formato utilizado en la interfaz.
      const items = await this.resolveCarouselVariants(rawItems.map(fromBackendItem));
      this.setState({ items });
    } catch (error) {
      console.error(
        "Error al obtener los elementos del carrusel:",
        JSON.stringify(error, null, 2)
      );
      // En caso de error se deja la lista vacía para no romper la vista.
      this.setState({ items: [] });
    } finally {
      this.setState({ loading: false });
      if (this.props.onLoading) this.props.onLoading(false);
    }
  }

  resolveCarouselVariants = async (items) => {
    const requestId = ++this._variantRequestId;
    const resolved = await Promise.all(
      (items || []).map(async (item) => {
        const uuid = item?.imageUuid;
        if (!uuid) return item;
        try {
          const variants = await getFileVariants(uuid);
          if (requestId !== this._variantRequestId) return item;
          const persisted = (variants || []).find((v) => {
            const key = String(v?.variantKey || "");
            const w = Number(v?.width);
            const h = Number(v?.height);
            if (key === `framed:${CAROUSEL_FORMAT_KEY}:${CAROUSEL_WIDTH}x${CAROUSEL_HEIGHT}`) {
              return true;
            }
            return key.startsWith("framed:")
              && Number.isFinite(w)
              && Number.isFinite(h)
              && Math.round(w) === CAROUSEL_WIDTH
              && Math.round(h) === CAROUSEL_HEIGHT;
          });

          if (persisted?.variantKey) {
            return {
              ...item,
              image: mfmVariantUrl(uuid, String(persisted.variantKey)),
            };
          }

          return {
            ...item,
            image: mfmFramedUrl(uuid, CAROUSEL_WIDTH, CAROUSEL_HEIGHT, CAROUSEL_FORMAT_KEY),
          };
        } catch {
          return {
            ...item,
            image: mfmFramedUrl(uuid, CAROUSEL_WIDTH, CAROUSEL_HEIGHT, CAROUSEL_FORMAT_KEY),
          };
        }
      }),
    );
    return resolved;
  };

  /**
   * Llama al servicio para crear o actualizar un elemento. Si el
   * elemento contiene un id se actualiza, de lo contrario se crea.
   */
  async saveItem(item) {
    // Convierte el elemento a la estructura que espera el backend y
    // realiza una llamada al servicio de upsert. Si no tiene id,
    // el backend lo insertará; de lo contrario, lo actualizará.
    this.setState({ loading: true });
    if (this.props.onLoading) this.props.onLoading(true);
    try {
      const payload = [toBackendItem(item)];
      const requestOptions = {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      };
      const response = await doFetch("TEAN", "upsertCarousel", requestOptions);
      const data = await response.json();
      if (data && data.STATE === "SUCCESS") {
        // Recargar los elementos para reflejar los cambios y volver a la tabla
        await this.loadItems();
        this.setState({
          showModal: true,
          modalContent: "table",
          editingItem: null,
        });
      } else {
        console.error("No se pudo guardar el elemento del carrusel.", data);
      }
    } catch (error) {
      console.error("Error al guardar el elemento del carrusel:", error);
    } finally {
      this.setState({ loading: false });
      if (this.props.onLoading) this.props.onLoading(false);
    }
  }

  /**
   * Llama al servicio para borrar un elemento existente.
   */
  async deleteItem() {
    const { itemToDelete } = this.state;
    if (!itemToDelete) return;

    this.setState({ loading: true });

    if (this.props.onLoading) this.props.onLoading(true);

    try {
      const requestOptions = {
        method: "DELETE",
      };
      let method = "deleteCarousel?IDCarousel=" + this.state.itemToDelete.id;

      const response = await doFetch("TEAN", method, requestOptions);
      const data = await response.json();
      if (data && data.STATE === "SUCCESS") {
        await this.loadItems();
        this.setState({ showDeleteConfirm: false, itemToDelete: null });
      } else {
        console.error("No se pudo borrar el elemento del carrusel.", data);
      }
    } catch (error) {
      console.error("Error al borrar el elemento del carrusel:", error);
    } finally {
      this.setState({ loading: false });
      if (this.props.onLoading) this.props.onLoading(false);
    }
  }

  /**
   * Cambia el estado activo/inactivo de un elemento.
   */
  async toggleActive(item) {
    this.setState({ loading: true });
    if (this.props.onLoading) this.props.onLoading(true);
    try {
      // Crear una copia del elemento con el estado invertido y enviarlo al backend
      const updatedItem = { ...item, active: !item.active };
      const payload = [toBackendItem(updatedItem)];
      const requestOptions = {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      };
      const response = await doFetch("TEAN", "upsertCarousel", requestOptions);
      const data = await response.json();
      if (data && data.STATE === "SUCCESS") {
        await this.loadItems();
      } else {
        console.error("No se pudo actualizar el estado del elemento.", data);
      }
    } catch (error) {
      console.error("Error al cambiar el estado del elemento:", error);
    } finally {
      this.setState({ loading: false });
      if (this.props.onLoading) this.props.onLoading(false);
    }
  }

  /**
   * Abre el modal en modo tabla.
   */
  openTableModal() {
    this.setState({
      showModal: true,
      modalContent: "table",
      editingItem: null,
    });
  }

  /**
   * Cierra el modal.
   */
  closeModal() {
    this.setState({
      showModal: false,
      modalContent: "table",
      editingItem: null,
    });
  }

  /**
   * Abre el formulario para editar un elemento existente o crear uno nuevo.
   */
  openForm(item = null) {
    this.setState({ modalContent: "form", editingItem: item });
  }

  /**
   * Maneja el envío del formulario. Recibe un evento y evita que se envíe
   * el formulario por defecto.
   */
  handleFormSubmit(event) {
    event.preventDefault();
    // Construir objeto a guardar en base al estado del formulario
    const { editingItem } = this.state;
    const id = editingItem && editingItem.id ? editingItem.id : null;
    const item = {
      id: id,
      imageUuid: this.imageUuid,
      title: this.title,
      description: this.description,
      link: this.link,
      active: this.active,
    };
    this.saveItem(item);
  }

  /**
   * Maneja los cambios en los controles del formulario. Se almacenan
   * temporalmente en propiedades de instancia para evitar múltiples
   * actualizaciones de estado durante la escritura. Cuando se envía
   * el formulario, se utilizan estos valores para construir el objeto
   * definitivo.
   */
  handleInputChange(field, value) {
    // Guardar valores temporales en propiedades de la clase
    this[field] = value;
  }

  renderCarousel() {
    return (
      <MDBRow className="carousel-admin-preview mb-3">
        <MDBCol>
          <CustomCarousel
            key={this.state.items.length}
            items={this.state.items}
            showControls={true}
            showIndicators={true}
            // Filtrar solo elementos activos para la vista principal
            filterActive={true}
          />
        </MDBCol>
      </MDBRow>
    );
  }

  renderTableModal() {
    return (
      <MDBModal
        show={this.state.showModal}
        staticBackdrop
        backdrop={true}
        size="xl"
      >
        <MDBModalDialog style={{ maxWidth: "90vw" }}>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>Elementos del Carrusel</MDBModalTitle>
              <MDBBtn
                className="btn-close"
                color="none"
                onClick={() => this.closeModal()}
              ></MDBBtn>
            </MDBModalHeader>
            <MDBModalBody>
              {/* Botón para añadir nuevo elemento */}
              <MDBRow className="mb-3">
                <MDBCol className="text-end">
                  <MDBBtn onClick={() => this.openForm(null)}>
                    Añadir elemento
                  </MDBBtn>
                </MDBCol>
              </MDBRow>
              <MDBTable align="middle" striped hover responsive>
                <MDBTableHead>
                  <tr>
                    <th scope="col" style={{ width: "20%" }}>
                      Imagen
                    </th>
                    <th scope="col" style={{ width: "20%" }}>
                      Título
                    </th>
                    <th scope="col" style={{ width: "30%" }}>
                      Descripción
                    </th>
                    <th scope="col" style={{ width: "10%" }}>
                      Activo
                    </th>
                    <th scope="col" style={{ width: "20%" }}>
                      Acciones
                    </th>
                  </tr>
                </MDBTableHead>
                <MDBTableBody>
                  {this.state.items.map((item, idx) => (
                    <tr key={idx}>
                      <td>
                        {item.image ? (
                          <img
                            src={item.image}
                            alt={item.title}
                            className="carousel-thumbnail"
                          />
                        ) : (
                          <div className="carousel-thumbnail" style={{ background: "#eee", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#aaa" }}>Sin imagen</div>
                        )}
                      </td>
                      <td>{item.title}</td>
                      <td
                        className="text-truncate"
                        style={{ maxWidth: "250px" }}
                      >
                        {item.description}
                      </td>
                      <td>
                        <MDBSwitch
                          id={`active-${item.id}`}
                          label=""
                          checked={item.active}
                          onChange={() => this.toggleActive(item)}
                        />
                      </td>
                      <td>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id={`tooltip-edit-${idx}`}>Editar</Tooltip>
                          }
                        >
                          <MDBBtn
                            color="primary"
                            outline
                            className="mx-1"
                            floating
                            onClick={() => this.openForm(item)}
                          >
                            <MDBIcon icon="edit" />
                          </MDBBtn>
                        </OverlayTrigger>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id={`tooltip-delete-${idx}`}>
                              Eliminar
                            </Tooltip>
                          }
                        >
                          <MDBBtn
                            color="danger"
                            outline
                            className="mx-1"
                            floating
                            onClick={() =>
                              this.setState({
                                showDeleteConfirm: true,
                                itemToDelete: item,
                              })
                            }
                          >
                            <MDBIcon icon="times" />
                          </MDBBtn>
                        </OverlayTrigger>
                      </td>
                    </tr>
                  ))}
                </MDBTableBody>
              </MDBTable>
            </MDBModalBody>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    );
  }

  renderFormModal() {
    // Preparar datos para el formulario: si hay item a editar, precargar
    const item = this.state.editingItem || {};
    // Establecer valores iniciales para propiedades temporales si no existen.
    // Para nuevos elementos se dejan los campos opcionales vacíos y el estado activo a false.
    this.imageUuid = item.imageUuid;
    this.title = item.title;
    this.description = item.description;
    this.link = item.link;
    this.active = item.active;
    return (
      <MDBModal
        show={this.state.showModal}
        staticBackdrop
        backdrop={true}
        size="lg"
      >
        <MDBModalDialog>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>
                {item && item.id
                  ? "Editar elemento"
                  : "Agregar elemento al carrusel"}
              </MDBModalTitle>
              <MDBBtn
                className="btn-close"
                color="none"
                onClick={() => this.closeModal()}
              ></MDBBtn>
            </MDBModalHeader>
            <MDBModalBody>
              <form onSubmit={(e) => this.handleFormSubmit(e)}>
                <MDBRow className="mb-3">
                  <MDBCol size="12" className="text-center">
                    <MfmImageUploader
                      uuid={this.imageUuid || null}
                      appCode="TEAN"
                      library="carousel"
                      size="original"
                      mediaType="image"
                      mediaFormat="1246/400"
                      mediaFrameSize="1246x400"
                      allowPicker
                      onUpload={(result) => {
                        this.imageUuid = result.uuid;
                        this.handleInputChange("imageUuid", result.uuid);
                      }}
                      onDelete={() => {
                        this.imageUuid = null;
                        this.handleInputChange("imageUuid", null);
                      }}
                      style={{ width: "100%", aspectRatio: "1246/400", borderRadius: 8 }}
                    />
                  </MDBCol>
                </MDBRow>
                <MDBRow className="mb-3">
                  <MDBCol size="12">
                    <MDBInput
                      type="text"
                      id="title"
                      label="Título"
                      defaultValue={this.title}
                      onChange={(e) =>
                        this.handleInputChange("title", e.target.value)
                      }
                    />
                  </MDBCol>
                </MDBRow>
                <MDBRow className="mb-3">
                  <MDBCol size="12">
                    <MDBInput
                      type="textarea"
                      id="description"
                      label="Descripción"
                      defaultValue={this.description}
                      onChange={(e) =>
                        this.handleInputChange("description", e.target.value)
                      }
                      rows={4}
                    />
                  </MDBCol>
                </MDBRow>
                <MDBRow className="mb-3">
                  <MDBCol size="12">
                    <MDBInput
                      type="text"
                      id="link"
                      label="Enlace"
                      defaultValue={this.link}
                      onChange={(e) =>
                        this.handleInputChange("link", e.target.value)
                      }
                    />
                  </MDBCol>
                </MDBRow>
                <MDBRow className="mb-3">
                  <MDBCol size="12">
                    <MDBSwitch
                      id="active"
                      label="Activo"
                      checked={this.active}
                      onChange={(e) => {
                        this.handleInputChange("active", e.target.checked);
                        this.active = e.target.checked;
                      }}
                    />
                  </MDBCol>
                </MDBRow>
                <MDBRow className="mt-4">
                  <MDBCol size="12" className="text-end">
                    <MDBBtn type="submit" className="me-2">
                      Guardar
                    </MDBBtn>
                    <MDBBtn
                      color="secondary"
                      onClick={() => this.setState({ modalContent: "table" })}
                    >
                      Cancelar
                    </MDBBtn>
                  </MDBCol>
                </MDBRow>
              </form>
            </MDBModalBody>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    );
  }

  render() {
    return (
      <div className="carousel-admin-section my-4">
        <MDBCard>
          <MDBCardHeader>
            <MDBRow>
              <MDBCol className="d-flex justify-content-between align-items-center">
                <span className="section-title">
                  Configuración del carrusel
                </span>
                <MDBBtn size="sm" onClick={() => this.openTableModal()}>
                  Editar carrusel
                </MDBBtn>
              </MDBCol>
            </MDBRow>
          </MDBCardHeader>
          <MDBCardBody>
            {!(this.state.showModal && this.state.modalContent === "form") && this.renderCarousel()}
          </MDBCardBody>
        </MDBCard>
        {/* Modal principal: cambia de contenido según sea tabla o formulario */}
        {this.state.showModal &&
          this.state.modalContent === "table" &&
          this.renderTableModal()}
        {this.state.showModal &&
          this.state.modalContent === "form" &&
          this.renderFormModal()}
        {/* Confirmación de borrado */}
        {this.state.showDeleteConfirm && (
          <PopConfirm
            show={this.state.showDeleteConfirm}
            title="Confirmar borrado"
            detail="¿Está seguro de que desea eliminar este elemento?"
            onConfirm={() => this.deleteItem()}
            onCancel={() =>
              this.setState({ showDeleteConfirm: false, itemToDelete: null })
            }
          />
        )}
      </div>
    );
  }
}

export default CarouselManager;
